// frontend/src/components/InboxWorkspace.jsx
// Version: 2.0 (quick-260915-did — выдуманные обращения убраны, лента только живая)
// Description: Единый центр входящих обращений (Sofia Voice, Email, SMS, Web Chat)
//              в эталонном дизайне (Quiet Luxury Editorial).
//
// 🔴 ЧТО УБРАНО 15.09.2026 И ПОЧЕМУ ЭТО САМОЕ ТЯЖЁЛОЕ МЕСТО. Зашитая
//    лента-«эталон» несла ПЯТЬ полных выдуманных обращений: имена, телефоны,
//    рабочий адрес почты, стенограммы звонков Sofia — и ГОТОВЫЕ ЧЕРНОВИКИ
//    ОТВЕТОВ КЛИЕНТУ с `canApprove: true`. Они подмешивались в ОДИН список с
//    настоящими письмами (`liveEmails` ← apiService.getPendingEmails) и несли
//    ТУ ЖЕ кнопку «Одобрить и отправить». Отличить выдуманное обращение от
//    настоящего оператору было НЕЧЕМ.
//
// 🔴 ДОКАЗАТЕЛЬСТВО, ЧТО ЭТО НЕ ТЕОРИЯ. В передаче на 15.09.2026 приоритетом
//    рабочего дня стояло «завести досье» по клиенту с визой O-1, которого не
//    существует: это была карточка ИЗ ЭТОГО ФАЙЛА. План рабочего дня оказался
//    построен на демо-данных, и никто этого не заметил — отличить было нечем.
//    Имена и номера здесь НЕ ПОВТОРЯЮТСЯ нарочно: их стережёт проба
//    `__tests__/vydumannaya-faktura.test.js`, и повтор в комментарии сделал бы
//    сторожа красным на собственном объяснении.
//
// 🔴 ХУЖЕ ТОГО: две карточки ссылались на НАСТОЯЩИЕ номера дел из боевой базы,
//    а одна несла адрес почты, похожий на почту настоящего клиента.
//    Выдуманное и настоящее были уже перемешаны.
//
// 🔴 УДАЛЕНО ЦЕЛИКОМ, А НЕ СПРЯТАНО ПОД ФЛАГ: константа, которую можно вернуть
//    одной строкой, возвращается; флаг, ничего не переключающий в бою, —
//    украшение ([[knowledge_decorative_flag]]).
//
// 🔴 ТРИ СОСТОЯНИЯ, А НЕ ДВА. «Спрашиваем» / «обращений нет» / «не удалось
//    получить: {причина}». Отказ ручки НЕ ИМЕЕТ ПРАВА выглядеть как «обращений
//    нет»: это тот самый род дефекта, из-за которого 395 обращений прошлой системы были
//    выброшены молча ([[feedback_unknown_is_not_no]], правило фирмы «ни одно
//    обращение не теряется»).
//
// 🔴 КНОПКА ОДОБРЕНИЯ — ТОЛЬКО У ЗАПИСИ С НАСТОЯЩИМ rawId. Одобрять нечего,
//    если записи нет на сервере; раньше выдуманные карточки показывали ту же
//    кнопку и «успешно отправлено» без единого обращения к бэкенду.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Phone,
  Mail,
  MessageSquare,
  Sparkles,
  ArrowLeft,
  CheckCircle,
  Clock,
  Search,
  Send,
  ShieldCheck,
  Building,
  User,
  AlertCircle,
  Inbox as InboxIcon,
  X
} from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useUiLanguage } from '../context/UiLanguageContext';
import { useBranding } from '../context/BrandingContext';
import { Button, Card, Badge, Input, Textarea } from './ui';

// Иконки и цветовые подложки каналов по дизайн-системе
const SOURCE_CONFIG = {
  voice: {
    label: 'Звонок AI Voice',
    Icon: Phone,
    colorClass: 'text-warning bg-warning-soft',
    borderClass: 'border-warning-border'
  },
  email: {
    label: 'Email',
    Icon: Mail,
    colorClass: 'text-info bg-info-soft',
    borderClass: 'border-info-border'
  },
  sms: {
    label: 'SMS',
    Icon: MessageSquare,
    colorClass: 'text-accent-ink bg-accent-soft',
    borderClass: 'border-accent-border'
  },
  chat: {
    label: 'Web Chat',
    Icon: MessageSquare,
    colorClass: 'text-accent-ink bg-accent-soft',
    borderClass: 'border-accent-border'
  }
};

export const InboxWorkspace = ({ onBack, onAskKatifi }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isManagingPartner, isStaff } = usePermissions();
  const { lang } = useUiLanguage();
  const { branding } = useBranding();

  // Состояния фильтров
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  // Ничего не выбрано, пока живой источник не ответил: прежнее 'in-1' было
  // идентификатором ПЕРВОЙ ВЫДУМАННОЙ карточки.
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [officeFilter, setOfficeFilter] = useState('all');

  // Данные.
  // 🔴 ТРИ состояния источника, а не два: `zagruzka` — ещё не спросили;
  //    `otkazIstochnika` — спросили и получили ОТКАЗ (с причиной);
  //    пустой `liveEmails` при обоих «нет» — спросили, обращений НЕТ.
  //    Слить второе с третьим значит выбросить обращение молча.
  const [liveEmails, setLiveEmails] = useState([]);
  const [officesList, setOfficesList] = useState([]);
  const [zagruzka, setZagruzka] = useState(true);
  const [otkazIstochnika, setOtkazIstochnika] = useState(null);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [isAutopilotModalOpen, setIsAutopilotModalOpen] = useState(false);
  const [editableDrafts, setEditableDrafts] = useState({});
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  useEffect(() => {
    let mounted = true;
    apiService.getOfisy().then((res) => {
      if (mounted && res && Array.isArray(res.ofisy)) {
        setOfficesList(res.ofisy);
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Загрузка реальных писем с бэкенда — ЕДИНСТВЕННЫЙ источник этой ленты
  const fetchEmails = useCallback(async () => {
    try {
      setZagruzka(true);
      const data = await apiService.getPendingEmails();
      // Пустой ответ обязан ОЧИЩАТЬ ленту. Прежнее `if (data.length > 0)`
      // оставляло на экране прошлую выдачу, и «обращений больше нет»
      // выглядело как «обращения всё ещё висят».
      const spisok = Array.isArray(data) ? data : [];
      // Преобразуем реальные письма в формат единого инбокса
      const defaultOfficeLabel = lang === 'en' ? 'Office not specified' : 'Офис не указан';
      const formatted = spisok.map((item) => ({
        id: `email-${item.id}`,
        rawId: item.id,
        name: item.ai_extracted?.client_name || item.sender || 'Неизвестный отправитель',
        contact: item.sender || item.ai_extracted?.client_phone || 'Email',
        officeId: item.office_id || null,
        officeName: item.office_name || defaultOfficeLabel,
        source: 'email',
        sourceLabel: 'Live Email',
        status: 'ready',
        time: item.created_at ? new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Только что',
        subject: item.subject || 'Новое обращение',
        summary: item.ai_summary || item.body?.slice(0, 150) || 'Письмо ожидает проверки',
        urgency: item.ai_extracted?.urgency === 'high' ? 'high' : 'normal',
        draft: item.ai_draft_reply || '',
        body: item.body || '',
        // 🔴 Одобрять можно ТОЛЬКО то, что есть на сервере: признак ВЫВОДИТСЯ
        //    из наличия настоящего идентификатора записи, а не пишется рукой.
        canApprove: item.id !== undefined && item.id !== null,
        isLiveBackend: true
      }));
      setLiveEmails(formatted);
      // Сбрасываем отказ ТОЛЬКО на удачном ответе: иначе прошлый отказ жил бы
      // вечно, а новый успех был бы не виден.
      setOtkazIstochnika(null);
    } catch (e) {
      console.error('Не удалось получить входящие обращения:', e);
      // Причину называем ДОСЛОВНО. «Что-то пошло не так» — второй способ
      // сказать «ничего»: он снова сливает отказ с пустотой.
      setOtkazIstochnika((e && e.message) || 'ручка входящих не ответила, причина не названа');
    } finally {
      setZagruzka(false);
    }
  }, [lang]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // Лента строится ТОЛЬКО из живого источника. Подмешивать в неё нечего.
  const allItems = useMemo(() => liveEmails, [liveEmails]);

  // Фильтрация
  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allItems.filter((item) => {
      const matchesSource = sourceFilter === 'all' || item.source === sourceFilter;
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'sent' ? approvedIds.has(item.id) : item.status === statusFilter && !approvedIds.has(item.id));
      const matchesOffice = officeFilter === 'all' || (Boolean(item.officeId) && item.officeId === officeFilter);
      const matchesSearch = !q ||
        (item.name && item.name.toLowerCase().includes(q)) ||
        (item.subject && item.subject.toLowerCase().includes(q)) ||
        (item.contact && item.contact.toLowerCase().includes(q)) ||
        (item.summary && item.summary.toLowerCase().includes(q));

      return matchesSource && matchesStatus && matchesOffice && matchesSearch;
    });
  }, [allItems, sourceFilter, statusFilter, officeFilter, searchQuery, approvedIds]);

  // Выбранный элемент
  const selectedItem = useMemo(() => {
    return filteredItems.find((it) => it.id === selectedId) || filteredItems[0] || null;
  }, [filteredItems, selectedId]);

  // Инициализация черновика для выбранного
  useEffect(() => {
    if (selectedItem && editableDrafts[selectedItem.id] === undefined) {
      setEditableDrafts((prev) => ({
        ...prev,
        [selectedItem.id]: selectedItem.draft || ''
      }));
    }
  }, [selectedItem, editableDrafts]);

  // 🔴 Одобрять можно ТОЛЬКО запись, которая есть на сервере. Прежнее условие
  //    вызывало бэкенд лишь для живых записей, а выдуманную карточку МОЛЧА
  //    проносило мимо и всё равно рапортовало «успешно отправлено» — обещание
  //    без исполнителя ([[knowledge_promise_is_not_a_gate]]). Теперь отсутствие
  //    записи на сервере — ОТКАЗ, а не тихий успех.
  const mozhnoOdobrit = (item) => Boolean(item && item.isLiveBackend && item.rawId !== undefined && item.rawId !== null);

  // Обработчик "Одобрить и отправить"
  const handleApproveAndSend = async (item) => {
    if (!item) return;
    if (!mozhnoOdobrit(item)) {
      toast.error('Одобрять нечего: этой записи нет на сервере.');
      return;
    }
    setIsProcessingAction(true);
    try {
      const draftText = editableDrafts[item.id] || item.draft;
      await apiService.approveEmail(item.rawId, draftText);
      setApprovedIds((prev) => new Set([...prev, item.id]));
      toast.success(`Ответ для ${item.name} успешно одобрен и отправлен!`);
    } catch (err) {
      toast.error(`Ошибка отправки: ${err.message}`);
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Обработчик "Отклонить"
  const handleDismiss = async (item) => {
    if (!item) return;
    if (!mozhnoOdobrit(item)) {
      toast.error('Отклонять нечего: этой записи нет на сервере.');
      return;
    }
    setIsProcessingAction(true);
    try {
      await apiService.dismissEmail(item.rawId);
      setApprovedIds((prev) => new Set([...prev, item.id]));
      toast.success(`Обращение от ${item.name} отклонено.`);
    } catch (err) {
      toast.error(`Ошибка: ${err.message}`);
    } finally {
      setIsProcessingAction(false);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 pt-24 lg:pt-6 pb-6 space-y-6 text-foreground min-w-0">
      {/* 1. Верхняя панель: Навигация и титул */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border">
        <div>
          {onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground mb-2 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Назад к обзору
            </button>
          )}
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-info">
            <InboxIcon className="w-3.5 h-3.5" />
            Единая входящая очередь • Omnichannel Intake
          </div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-display font-normal text-foreground">
            Входящие обращения
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Сначала понять суть запроса, затем подтвердить одно точное следующее действие.
          </p>
        </div>

        {/* Быстрые действия */}
        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAutopilotModalOpen(true)}
            style={{
              backgroundColor: 'var(--surface-raised)',
              borderColor: 'var(--border)'
            }}
            className="gap-1.5 text-xs font-semibold shadow-sm"
          >
            <ShieldCheck className="w-4 h-4 text-success" />
            Правила автопилота
          </Button>
          <Button
            variant="accent"
            size="sm"
            onClick={() => {
              if (onAskKatifi) {
                onAskKatifi('Покажи самые срочные входящие и предложи оптимальный порядок обработки.');
              } else {
                toast('Katifi анализирует приоритеты обращений...', { icon: '✨' });
              }
            }}
            className="gap-1.5 text-xs font-bold shadow-sm"
          >
            <Sparkles className="w-4 h-4" />
            Расставить приоритеты с Katifi
          </Button>
        </div>
      </div>

      {/* 2. Строка фильтров по офисам и каналам */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
        {/* Поиск */}
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="text"
            placeholder="Поиск по имени, теме или номеру..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              paddingLeft: '2.5rem',
              borderRadius: 'var(--radius-xl, 0.75rem)',
              backgroundColor: 'var(--surface-raised)',
              borderColor: 'var(--border)'
            }}
            className="w-full h-10 pr-4 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground shadow-sm"
          />
        </div>

        {/* Выбор филиала */}
        <div>
          <div className="relative">
            <Building className="w-4 h-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={officeFilter}
              onChange={(e) => setOfficeFilter(e.target.value)}
              className="w-full h-10 pl-10 pr-3 bg-card border border-border rounded-xl text-xs font-semibold text-foreground focus:outline-none focus:border-accent shadow-sm appearance-none cursor-pointer"
            >
              <option value="all">Все офисы</option>
              {officesList.map((o) => (
                <option key={o.kluch} value={o.kluch}>
                  {o.nazvanie || o.kluch}{o.glavnyy ? ' (главный)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Статус обращения */}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full h-10 px-3 bg-card border border-border rounded-xl text-xs font-semibold text-foreground focus:outline-none focus:border-accent shadow-sm appearance-none cursor-pointer"
          >
            <option value="all">Все статусы</option>
            <option value="ready">Готовы к отправке (Ready)</option>
            <option value="review">На проверке (Review)</option>
            <option value="new">Новые (New)</option>
            <option value="sent">Отправленные (Sent)</option>
          </select>
        </div>
      </div>

      {/* 3. Табы каналов (Все, Голос, Email, SMS, Чат) */}
      <div className="w-full max-w-full min-w-0 overflow-x-auto pb-1 text-xs font-bold border-b border-border">
        <div className="flex gap-2 min-w-max">
          {[
            { id: 'all', label: 'Все источники', count: allItems.length },
            { id: 'voice', label: 'Звонки Sofia Voice', count: allItems.filter(i => i.source === 'voice').length },
            { id: 'email', label: 'Email', count: allItems.filter(i => i.source === 'email').length },
            { id: 'sms', label: 'SMS', count: allItems.filter(i => i.source === 'sms').length },
            { id: 'chat', label: 'Web Chat', count: allItems.filter(i => i.source === 'chat').length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSourceFilter(tab.id)}
              className={`px-3.5 py-2 rounded-lg transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
                sourceFilter === tab.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-extrabold ${
                sourceFilter === tab.id ? 'bg-accent text-accent-ink' : 'bg-secondary text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 4. Основная сетка: Список (слева) + Детали и черновик (справа) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Левая колонка: Карточки обращений */}
        <div className="lg:col-span-5 space-y-3">
          {/* 🔴 ТРИ ВИДА, А НЕ ДВА. «Спрашиваем», «не удалось получить — с
              причиной» и «обращений нет» — три РАЗНЫХ ответа, и одинаковыми
              словами они не говорят. Раньше второй молча выглядел как третий. */}
          {zagruzka ? (
            <Card
              className="bg-card p-8 text-center space-y-2"
              style={{
                borderRadius: 'var(--radius-xl, 0.75rem)',
                borderColor: 'var(--border)'
              }}
            >
              <Clock className="w-8 h-8 text-muted-foreground mx-auto opacity-50" />
              <p className="text-xs font-semibold text-muted-foreground">
                Загружается…
              </p>
            </Card>
          ) : otkazIstochnika ? (
            <Card
              className="bg-card p-8 text-center space-y-2"
              style={{
                borderRadius: 'var(--radius-xl, 0.75rem)',
                borderColor: 'color-mix(in srgb, var(--danger) 40%, transparent)'
              }}
            >
              <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
              <p className="text-xs font-semibold text-destructive leading-relaxed">
                Не удалось получить входящие обращения: {otkazIstochnika}
              </p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Это НЕ «обращений нет». Список неизвестен — пока ручка не
                ответит, считайте, что обращения есть и их никто не видит.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchEmails}
                className="mt-1 text-xs font-bold"
              >
                Спросить ещё раз
              </Button>
            </Card>
          ) : filteredItems.length === 0 ? (
            <Card
              className="bg-card p-8 text-center space-y-2"
              style={{
                borderRadius: 'var(--radius-xl, 0.75rem)',
                borderColor: 'var(--border)'
              }}
            >
              <InboxIcon className="w-8 h-8 text-muted-foreground mx-auto opacity-50" />
              <p className="text-xs font-semibold text-muted-foreground">
                Нет входящих обращений для выбранных фильтров.
              </p>
            </Card>
          ) : (
            filteredItems.map((item) => {
              const srcConfig = SOURCE_CONFIG[item.source] || SOURCE_CONFIG.email;
              const IconComp = srcConfig.Icon;
              const isSelected = selectedItem && selectedItem.id === item.id;
              const isApproved = approvedIds.has(item.id);

              return (
                <Card
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  className="p-4 transition-all cursor-pointer text-left"
                  style={{
                    borderRadius: 'var(--radius-xl, 0.75rem)',
                    backgroundColor: 'var(--surface-raised)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: isSelected ? 'var(--accent)' : 'var(--border)',
                    boxShadow: isSelected
                      ? '0 0 0 1px var(--accent), var(--shadow-md)'
                      : 'var(--shadow-sm)'
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${srcConfig.colorClass} ${srcConfig.borderClass}`}>
                        <IconComp className="w-4 h-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-foreground truncate">
                          {item.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {item.contact}
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-[10px] text-muted-foreground font-medium block">
                        {item.time}
                      </span>
                      {item.urgency === 'high' && (
                        <Badge
                          variant="accent"
                          style={{
                            borderRadius: 'var(--radius-md, 6px)',
                            backgroundColor: 'var(--accent-soft)',
                            color: 'var(--accent-ink)',
                            borderColor: 'var(--accent-border)'
                          }}
                          className="text-[9px] px-1.5 py-0.5 mt-1 font-bold inline-block"
                        >
                          Срочно
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="mt-2.5">
                    <div className="text-xs font-semibold text-foreground line-clamp-1">
                      {item.subject}
                    </div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2 mt-1 leading-relaxed">
                      {item.summary}
                    </div>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-border/60 flex items-center justify-between text-[10px]">
                    <span className="text-muted-foreground font-medium flex items-center gap-1">
                      <Building className="w-3 h-3 text-muted-foreground" />
                      {item.officeName || (lang === 'en' ? 'Office not specified' : 'Офис не указан')}
                    </span>
                    {isApproved ? (
                      <span className="text-success font-bold flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Отправлено
                      </span>
                    ) : item.status === 'ready' ? (
                      <span className="text-info font-bold">
                        Черновик готов
                      </span>
                    ) : (
                      <span className="text-warning font-bold">
                        На рассмотрении
                      </span>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Правая колонка: Детальный просмотр и карточка одобрения */}
        <div className="lg:col-span-7">
          {selectedItem ? (
            <Card
              className="bg-card p-5 space-y-5 shadow-sm"
              style={{
                borderRadius: 'var(--radius-xl, 0.75rem)',
                borderColor: 'var(--border)'
              }}
            >
              {/* Шапка выбранного обращения */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-4 border-b border-border">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-foreground">
                      {selectedItem.name}
                    </h2>
                    <Badge
                      variant="secondary"
                      style={{
                        borderRadius: 'var(--radius-md, 6px)',
                        backgroundColor: 'var(--surface-sunken)',
                        color: 'var(--text-muted)',
                        borderColor: 'var(--border)'
                      }}
                      className="text-[11px] px-2 py-0.5 font-semibold"
                    >
                      {selectedItem.officeName || (lang === 'en' ? 'Office not specified' : 'Офис не указан')}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {selectedItem.contact} • {selectedItem.sourceLabel} • {selectedItem.time}
                  </div>
                </div>

                {selectedItem.existingClient ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/cases')}
                    style={{
                      backgroundColor: 'var(--info-soft)',
                      color: 'var(--info)',
                      borderColor: 'color-mix(in srgb, var(--info) 30%, transparent)'
                    }}
                    className="text-xs font-bold"
                  >
                    {selectedItem.existingClient} →
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const nameParts = (selectedItem.name || '').trim().split(' ');
                      const firstName = nameParts[0] || '';
                      const lastName = nameParts.slice(1).join(' ') || '';
                      const isEmail = (selectedItem.contact || '').includes('@');
                      navigate('/cases/new', {
                        state: {
                          clientFirstName: firstName,
                          clientLastName: lastName,
                          clientPhoneNumber: isEmail ? '' : (selectedItem.contact || ''),
                          clientEmail: isEmail ? (selectedItem.contact || '') : '',
                          title: selectedItem.subject || `Дело: ${selectedItem.name || 'Новый клиент'}`,
                          description: selectedItem.summary || ''
                        }
                      });
                    }}
                    style={{
                      backgroundColor: 'var(--accent-soft)',
                      color: 'var(--accent-ink)',
                      borderColor: 'var(--accent-border)'
                    }}
                    className="text-xs font-bold"
                  >
                    + Создать досье клиента
                  </Button>
                )}
              </div>

              {/* Тема обращения */}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  Тема обращения
                </div>
                <div className="text-sm font-bold text-foreground mt-0.5">
                  {selectedItem.subject}
                </div>
              </div>

              {/* Блок транскрипта звонка или текста сообщения */}
              {selectedItem.transcript ? (
                <div className="bg-secondary border border-border rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                    <Phone className="w-3.5 h-3.5 text-warning" />
                    Стенограмма звонка (Sofia Voice)
                  </div>
                  <div className="text-xs text-foreground leading-relaxed whitespace-pre-line font-mono bg-card p-3 rounded-lg border border-border/60">
                    {selectedItem.transcript}
                  </div>
                </div>
              ) : selectedItem.body ? (
                <div className="bg-secondary border border-border rounded-xl p-4 space-y-2">
                  <div className="text-xs font-bold text-foreground">
                    Текст входящего письма
                  </div>
                  <div className="text-xs text-foreground leading-relaxed whitespace-pre-line bg-card p-3 rounded-lg border border-border/60">
                    {selectedItem.body}
                  </div>
                </div>
              ) : null}

              {/* Анализ Katifi */}
              <div className="bg-accent-soft border border-accent-border rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-bold text-accent-ink">
                    <Sparkles className="w-4 h-4 text-accent" />
                    Анализ Katifi
                  </div>
                  <Badge
                    variant="outline"
                    style={{
                      backgroundColor: 'var(--surface-raised)',
                      color: 'var(--accent-ink)',
                      borderColor: 'var(--accent-border)'
                    }}
                    className="text-[10px] font-extrabold px-2 py-0.5"
                  >
                    {selectedItem.urgency === 'high' ? 'Высокий приоритет' : 'Стандартный интейк'}
                  </Badge>
                </div>
                <p className="text-xs text-foreground leading-relaxed">
                  {selectedItem.summary}
                </p>
              </div>

              {/* Блок подготовленного ответа и действий */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5 text-info" />
                    Подготовленный черновик ответа
                  </label>
                  <span className="text-[11px] text-muted-foreground">
                    Требуется утверждение адвоката
                  </span>
                </div>

                <Textarea
                  rows={4}
                  value={editableDrafts[selectedItem.id] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditableDrafts((prev) => ({ ...prev, [selectedItem.id]: val }));
                  }}
                  style={{
                    backgroundColor: 'var(--surface)',
                    borderColor: 'var(--border)',
                    borderRadius: 'var(--radius-xl, 0.75rem)',
                    padding: '0.75rem',
                    fontSize: '0.75rem',
                    lineHeight: '1.5'
                  }}
                  className="w-full text-foreground transition-all"
                  placeholder="Введите или скорректируйте ответ..."
                />

                {/* Кнопки действий */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (onAskKatifi) {
                        onAskKatifi(`Помоги составить юридически выверенный ответ для клиента ${selectedItem.name} по теме: ${selectedItem.subject}`);
                      } else {
                        toast(`Katifi готовит персональный ответ для ${selectedItem.name}...`, { icon: '✨' });
                      }
                    }}
                    style={{
                      padding: 0,
                      height: 'auto',
                      minHeight: 0,
                      color: 'var(--accent-ink)',
                      backgroundColor: 'transparent',
                      borderColor: 'transparent'
                    }}
                    className="text-xs font-bold inline-flex items-center gap-1.5"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-accent" />
                    Спросить Katifi по этому обращению
                  </Button>

                  {!mozhnoOdobrit(selectedItem) ? (
                    <span className="text-xs font-bold text-destructive">
                      Одобрять нечего: записи нет на сервере
                    </span>
                  ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDismiss(selectedItem)}
                      disabled={isProcessingAction}
                      style={{
                        backgroundColor: 'var(--surface-raised)',
                        borderColor: 'var(--border)',
                        color: 'var(--text-muted)'
                      }}
                      className="text-xs font-bold"
                    >
                      Отклонить
                    </Button>
                    <Button
                      variant={approvedIds.has(selectedItem.id) ? "default" : "accent"}
                      size="sm"
                      onClick={() => handleApproveAndSend(selectedItem)}
                      disabled={isProcessingAction || approvedIds.has(selectedItem.id)}
                      style={approvedIds.has(selectedItem.id) ? {
                        backgroundColor: 'var(--success)',
                        color: 'var(--on-primary)',
                        borderColor: 'var(--success)',
                        cursor: 'default',
                        opacity: 1
                      } : undefined}
                      className="text-xs font-bold shadow-sm inline-flex items-center gap-1.5"
                    >
                      {approvedIds.has(selectedItem.id) ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Отправлено
                        </>
                      ) : (
                        <>
                          <Send className="w-3.5 h-3.5" />
                          {isProcessingAction ? 'Отправка...' : 'Одобрить и отправить'}
                        </>
                      )}
                    </Button>
                  </div>
                  )}
                </div>
              </div>
            </Card>
          ) : (
            <Card
              className="p-12 text-center text-xs leading-relaxed bg-card"
              style={{
                borderRadius: 'var(--radius-xl, 0.75rem)',
                borderColor: otkazIstochnika
                  ? 'color-mix(in srgb, var(--danger) 40%, transparent)'
                  : 'var(--border)',
                color: otkazIstochnika ? 'var(--danger)' : 'var(--text-muted)'
              }}
            >
              {zagruzka
                ? 'Загружается…'
                : otkazIstochnika
                ? `Не удалось получить входящие обращения: ${otkazIstochnika}`
                : 'Выберите обращение из списка слева для просмотра деталей и подготовки ответа.'}
            </Card>
          )}
        </div>
      </div>

      {/* 5. Модальное окно правил автопилота */}
      {isAutopilotModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card
            className="border-border max-w-lg w-full p-6 space-y-4 shadow-2xl relative bg-card"
            style={{ borderRadius: 'var(--radius-2xl, 1rem)' }}
          >
            <button
              onClick={() => setIsAutopilotModalOpen(false)}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2.5 text-success">
              <ShieldCheck className="w-6 h-6" />
              <h3 className="text-base font-bold text-foreground">
                {branding?.company_name ? `Защитные правила автопилота ${branding.company_name}` : 'Защитные правила автопилота'}
              </h3>
            </div>

            <div className="text-xs text-muted-foreground space-y-3 leading-relaxed">
              <p>
                <strong className="text-foreground">1. Запрет юридических советов без адвоката (Anti-UPL):</strong> Все автоматические сообщения и черновики Katifi носят исключительно организационный характер (подтверждение слота, ссылка на защищенную оплату, запрос документов).
              </p>
              <p>
                <strong className="text-foreground">2. Human-in-the-Loop обязателен:</strong> Любое предметное письмо по делу клиента формируется как черновик и блокируется до явного нажатия кнопки «Одобрить и отправить» адвокатом.
              </p>
              <p>
                <strong className="text-foreground">3. Фиксация в летописи:</strong> Каждый звонок Sofia Voice транскрибируется, маркируется признаками срочности и сохраняется в сквозную летопись дела.
              </p>
            </div>

            <Button
              variant="primary"
              onClick={() => setIsAutopilotModalOpen(false)}
              className="w-full text-xs font-bold"
            >
              Понятно
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InboxWorkspace;
